import { useEffect, useState } from 'react';
import { shootCoins, shootProgress } from '@/core/domain/studio';
import { useBack } from '@/ui/back/backStack';
import { Icon } from '@/ui/components/Icon';
import { useReducedMotion } from '@/ui/hooks/useCountUp';
import { useI18n } from '@/ui/i18n/I18nProvider';
import { useSave, useSaveDerived, useServices } from '@/ui/services/ServicesContext';
import { criterionText } from '@/ui/studio/criteria';
import { newTxId } from '@/ui/util/ids';
import { NpcFace } from './OrdersSheet';
import modal from './UnlockModal.module.css';
import styles from './StudioShoot.module.css';

/** Narrow screens where the brief starts folded (same breakpoint as the docked panels, ux-screens §2.2). */
const COMPACT_QUERY = '(max-width: 599px)';

/**
 * The brief on the set (studio-nano.md §4): who is posing, what they asked for, a checklist that ticks
 * itself as props are placed, and the big "Chụp!". Shown in the studio while a shoot is being set up.
 */
export function ShootBrief({ onShot }: { onShot: (levelUp: number | null) => void }) {
  const { t } = useI18n();
  const { content, dispatch } = useServices();
  const session = useSave((s) => (s.studio.session?.phase === 'setup' ? s.studio.session : null));
  const inStudio = useSave((s) => s.lastSession.sceneId === 'studio');
  const def = session ? content.studioShoots.get(session.shootId) : undefined;
  const ticks = useSaveDerived((s) =>
    def ? shootProgress(content, s, def).map((p) => `${String(p.have)}/${String(p.need)}`) : [],
  );
  // Phones start with a one-line card so the set stays visible; tap it for the whole brief.
  const [open, setOpen] = useState(() => !window.matchMedia(COMPACT_QUERY).matches);
  if (!session || !def || !inStudio) return null;
  const npc = t(content.npcs.get(session.npcId)?.nameKey ?? '');
  const met = ticks.filter((x) => {
    const [have, need] = x.split('/');
    return Number(have) >= Number(need);
  }).length;
  const shoot = (
    <button
      type="button"
      className={styles.shoot}
      data-testid="shoot-take"
      onClick={() => {
        const r = dispatch({ type: 'finishShoot', txId: newTxId() });
        if (!r.ok) return;
        const up = r.events.flatMap((e) => (e.type === 'STUDIO_LEVEL_REACHED' ? [e.level] : []));
        onShot(up.length > 0 ? Math.max(...up) : null);
      }}
    >
      <Icon name="camera" size={26} />
      {t('ui.studio.shoot')}
    </button>
  );

  return (
    <div className={styles.wrap}>
      <section
        className={styles.brief}
        aria-label={t('ui.studio.brief_label', { npc })}
        data-testid="shoot-brief"
        data-open={open || undefined}
      >
        <div className={styles.headRow}>
          <button
            type="button"
            className={styles.head}
            aria-expanded={open}
            onClick={() => {
              setOpen((v) => !v);
            }}
          >
            <NpcFace npcId={session.npcId} initial={npc.split(' ').pop()?.slice(0, 1) ?? ''} />
            <span className={styles.who}>
              <strong>{t('ui.studio.brief_title', { npc })}</strong>
              <span className={styles.count}>
                {open
                  ? t('ui.tasks.progress', { current: met, target: def.criteria.length })
                  : t('ui.studio.brief_collapsed', { current: met, target: def.criteria.length })}
              </span>
            </span>
          </button>
          {/* Always in the top row, right under the HUD, so the bottom bar can never cover it. */}
          {shoot}
        </div>
        {open && (
          <div className={styles.details}>
            <p className={styles.quote}>“{t(def.briefKey)}”</p>
            <ul className={styles.list}>
              {def.criteria.map((c, i) => {
                const [have, need] = (ticks[i] ?? '0/1').split('/').map(Number);
                const ok = (have ?? 0) >= (need ?? 1);
                return (
                  <li key={i} data-ok={ok || undefined}>
                    <span className={styles.box} aria-hidden="true">
                      {ok && <Icon name="check" size={18} />}
                    </span>
                    <span>{criterionText(c, t, content)}</span>
                    {(need ?? 1) > 1 && (
                      <span className={styles.have}>
                        {t('ui.tasks.progress', { current: have ?? 0, target: need ?? 1 })}
                      </span>
                    )}
                  </li>
                );
              })}
            </ul>
            <p className={styles.hint}>{t('ui.studio.hint')}</p>
            <div className={styles.actions}>
              <button
                type="button"
                className={styles.secondary}
                onClick={() => {
                  dispatch({ type: 'cancelShoot' });
                }}
              >
                {t('ui.studio.cancel')}
              </button>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}

/** "Tách!": a soft white flash over the whole game when the photo is taken (none with reduced motion). */
export function ShutterFlash() {
  const { bus } = useServices();
  const reduced = useReducedMotion();
  const [n, setN] = useState(0);
  useEffect(
    () =>
      bus.onAny((e) => {
        if (e.type === 'SHOOT_COMPLETED') setN((x) => x + 1);
      }),
    [bus],
  );
  if (n === 0 || reduced) return null;
  return <div key={n} className={styles.flash} aria-hidden="true" />;
}

/**
 * The result card (studio-nano.md §4.3): only praise, what it paid, the client's happy line. The client keeps
 * posing for a souvenir photo until "Xong".
 */
export function ShootResult({ levelUp, onPhoto }: { levelUp: number | null; onPhoto: () => void }) {
  const { t } = useI18n();
  const { content, dispatch } = useServices();
  const session = useSave((s) => (s.studio.session?.phase === 'done' ? s.studio.session : null));
  const inStudio = useSave((s) => s.lastSession.sceneId === 'studio');
  const canPhoto = useSave((s) => s.features.includes('photoMode'));
  const done = () => {
    dispatch({ type: 'endShoot' });
  };
  useBack(done, session !== null && inStudio);
  const def = session ? content.studioShoots.get(session.shootId) : undefined;
  if (!session || !def || !inStudio) return null;
  const grade = session.grade ?? 'cute';
  const B = content.balance.studio;
  const stars = grade === 'excellent' ? 3 : grade === 'very_cute' ? 2 : 1;
  const npc = t(content.npcs.get(session.npcId)?.nameKey ?? '');
  return (
    <div className={modal.scrim}>
      <section
        className={modal.modal}
        role="dialog"
        aria-modal="true"
        aria-labelledby="shoot-result-title"
        data-testid="shoot-result"
      >
        <h2 id="shoot-result-title" className={modal.title}>
          {t('ui.studio.result.title')}
        </h2>
        <p className={styles.grade} data-grade={grade}>
          <span className={styles.stars} aria-hidden="true">
            {[1, 2, 3].map((i) => (
              <Icon key={i} name="star" size={30} className={i <= stars ? styles.starOn : styles.starOff} />
            ))}
          </span>
          {t(`studio.grade.${grade}`)}
        </p>
        <p className={styles.react}>
          <NpcFace npcId={session.npcId} initial={npc.split(' ').pop()?.slice(0, 1) ?? ''} />
          <span>“{t(`studio.react.${session.npcId}.${grade}`)}”</span>
        </p>
        <ul className={modal.lines}>
          <li>{t('ui.studio.result.coins', { coins: shootCoins(content, def, grade) })}</li>
          <li>{t('ui.studio.result.rep', { rep: B.reputation[grade] })}</li>
          <li>
            {t('ui.studio.result.hearts', {
              npc,
              n: B.friendship + (grade === 'excellent' ? B.excellentFriendshipBonus : 0),
            })}
          </li>
          {levelUp !== null && (
            <li className={styles.levelUp}>{t('ui.studio.level_up', { level: levelUp })}</li>
          )}
        </ul>
        <div className={modal.actions}>
          {canPhoto ? (
            <button type="button" className={modal.secondary} data-testid="shoot-photo" onClick={onPhoto}>
              {t('ui.studio.result.photo')}
            </button>
          ) : (
            <span />
          )}
          <button type="button" className={modal.primary} data-testid="shoot-done" onClick={done}>
            {t('ui.studio.result.done')}
          </button>
        </div>
      </section>
    </div>
  );
}
